import React from "react";
import { useI18n } from "../../i18n";
import { formatClock } from "../../utils/dates";
import Sheet from "./Sheet";
import { formatAmount, formatMoney } from "../utils/money";

const SummaryTile = ({ label, value, tone = "default" }) => (
  <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-3">
    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
      {label}
    </p>
    <p
      className={`text-lg font-extrabold tabular-nums mt-0.5 ${
        tone === "money" ? "text-emerald-400" : "text-white"
      }`}
    >
      {value}
    </p>
  </div>
);

/**
 * The day at the register: totals for cashing up, the sales just rung up, and
 * anything that failed to sync so it never disappears silently.
 */
const RecentSalesSheet = ({
  open,
  onClose,
  currency,
  summary,
  sales,
  loading,
  pendingCount,
  failed,
  onDiscardFailed,
}) => {
  const { t } = useI18n();
  const methodLabel = (method) => {
    const key = `common.payment.${method}`;
    const label = t(key);
    return label === key ? method : label;
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("pos.recent.title")}
      subtitle={t("pos.recent.subtitle")}
    >
      <div className="p-4 grid grid-cols-3 gap-2">
        <SummaryTile
          label={t("pos.recent.takings")}
          value={formatAmount(summary.revenue)}
          tone="money"
        />
        <SummaryTile label={t("pos.recent.sales")} value={summary.sales_count} />
        <SummaryTile
          label={t("pos.recent.average")}
          value={formatAmount(summary.average_sale)}
        />
      </div>
      <p className="px-4 pb-2 text-[11px] text-slate-500">
        {t("pos.recent.takingsHint")}
      </p>

      {/* Credit is kept out of the takings above: shown beside them so the
          count in the drawer still matches, and what is owed is not lost. */}
      {(summary.credit_collected > 0 ||
        summary.credit_given > 0 ||
        summary.credit_outstanding > 0) && (
        <div className="px-4 pb-3 grid grid-cols-3 gap-2">
          <SummaryTile
            label={t("pos.recent.collectedOnTabs")}
            value={formatAmount(summary.credit_collected || 0)}
          />
          <SummaryTile
            label={t("pos.recent.putOnCredit")}
            value={formatAmount(summary.credit_given || 0)}
          />
          <SummaryTile
            label={t("pos.recent.owedTotal")}
            value={formatAmount(summary.credit_outstanding || 0)}
          />
        </div>
      )}

      <div className="px-4 pb-2 flex flex-wrap gap-2">
        {Object.entries(summary.by_payment_method || {}).map(([method, data]) => (
          <span
            key={method}
            className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold text-slate-300"
          >
            {methodLabel(method)}:{" "}
            <span className="text-white tabular-nums">
              {formatAmount(data.revenue)}
            </span>{" "}
            <span className="text-slate-500">({data.sales_count})</span>
          </span>
        ))}
      </div>

      {pendingCount > 0 && (
        <p className="mx-4 mb-2 px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-500/40 text-xs font-semibold text-amber-300">
          {t("pos.recent.pending", { count: pendingCount })}
        </p>
      )}

      {failed.length > 0 && (
        <div className="mx-4 mb-3 rounded-xl bg-red-500/10 border border-red-500/40 overflow-hidden">
          <p className="px-3 py-2 text-xs font-bold text-red-300 border-b border-red-500/30">
            {t("pos.recent.rejected")}
          </p>
          <ul>
            {failed.map((entry) => (
              <li
                key={entry.queuedAt}
                className="flex items-center gap-3 px-3 py-2 border-b border-red-500/20 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-white">
                    {formatClock(entry.queuedAt)} ·{" "}
                    {t("pos.recent.lines", { count: entry.payload.items.length })}
                  </p>
                  <p className="text-[11px] text-red-300 truncate">{entry.error}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onDiscardFailed(entry.queuedAt)}
                  className="shrink-0 h-8 px-3 rounded-lg bg-slate-800 text-[11px] font-bold text-slate-300 hover:bg-slate-700"
                >
                  {t("common.dismiss")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h3 className="px-4 pt-2 pb-1 text-xs font-bold uppercase tracking-widest text-slate-400">
        {t("pos.recent.latest")}
      </h3>

      {loading ? (
        <p className="px-4 py-8 text-center text-sm text-slate-400">
          {t("common.loading")}
        </p>
      ) : sales.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-400">
          {t("pos.recent.noneToday")}
        </p>
      ) : (
        <ul className="pb-2">
          {sales.map((sale) => (
            <li
              key={sale.id}
              className="px-4 py-3 border-b border-slate-800 last:border-0"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white">
                    {sale.receipt_number
                      ? `#${sale.receipt_number}`
                      : t("pos.recent.sale")}
                    <span className="ms-2 text-xs font-normal text-slate-400">
                      {formatClock(sale.created_at)}
                    </span>
                  </p>
                  <p className="text-xs text-slate-400 truncate mt-0.5">
                    {(sale.items || [])
                      .map((item) => `${item.quantity}× ${item.product_name}${item.is_dose ? ` · ${t("doses.dose")}` : ""}`)
                      .join(", ")}
                  </p>
                </div>
                <div className="text-end shrink-0">
                  <p className="text-sm font-bold text-emerald-400 tabular-nums">
                    {formatMoney(sale.total_amount, currency)}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {methodLabel(sale.payment_method)}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
};

export default RecentSalesSheet;
